import {isEqual} from 'lodash';
import {Key} from 'ts-key-enum';

import {InboxesSearchSuggestionItemProps} from '../../../src/components/inboxes/search/searchBar/suggestions/inboxesSearchSuggestionsDropdownCommons';
import {SmartMixClassnamesEnum} from '../../../src/models/api/smartMixMatchModel';
import {ModifiersEnum} from './searchModifiersHelpers';
import {buildSearchToken, findActiveToken, SearchToken, splitSearchQuery} from './searchQueryHelpers';

/*
 * Types.
 */

type SuggestionSelectHandlerTrackingOptions = {
  isRecentQuery?: boolean;
  isSmartMixItem?: boolean;
  smartMixType?: SmartMixClassnamesEnum;
  isModifierSuggestion?: boolean;
  modifierSuggestionType?: ModifiersEnum;
  isConversationSuggestion?: boolean;
  conversationClassificationLabels?: ReadonlyArray<string>;
  modifierHelperType?: ModifiersEnum;
};

export type SuggestionSelectHandlerOptions = SuggestionSelectHandlerTrackingOptions & {
  insertWhiteSpace?: boolean;
  replaceWholeQuery?: boolean;
  shouldSaveOriginalQuery?: boolean;
};

export type SuggestionSelectHandler = (
  text: string,
  selectKey?: Key,
  options?: SuggestionSelectHandlerOptions,
) => void;

export interface SearchSuggestionsBaseProps {
  query?: string;
  activeToken: SearchToken;
  onSuggestionSelect: SuggestionSelectHandler;
}

/*
 * Constants.
 */

export const maxSearchSuggestions = 5;

export const fuzzySortBaseOptions = {
  allowTypo: true,
  limit: maxSearchSuggestions,
  threshold: -20,
};

const TypeValues = ['email', 'facebook', 'googleplay', 'intercom', 'sms', 'tweet', 'tweet_dm'];
const HasValues = ['attachments', 'calendar', 'draft', 'no-tags'];
const IsValues = [
  'assigned',
  'archived',
  'discussion',
  'open',
  'shared-with-me',
  'snoozed',
  'spam',
  'starred',
  'subscribed',
  'trashed',
  'unassigned',
  'unread',
  'unreplied',
  'unsubscribed',
];
const InValues = ['all', 'comments', 'messages', 'attachments'];
const CommonDatesValues = ['last-week'];

const CommonDatesPlaceholders = ['yyyy-mm-dd', 'yyyy-mm'];

/*
 * Helpers.
 */

export function insertSuggestionInPlace(
  query: string,
  newTokenText: string,
  offset = query.length,
  shouldInsertBlank = false,
): string {
  const activeToken = findActiveToken(query, offset);

  return splitSearchQuery(query)
    .map(({text, token}, partIndex, allParts) => {
      if (token && isEqual(token, activeToken)) {
        const maybeBlank = shouldInsertBlank && partIndex === allParts.length - 1 ? ' ' : '';
        return newTokenText + maybeBlank;
      }

      return text;
    })
    .join('');
}

export function shouldInsertWhitespace(value: string) {
  const token = buildSearchToken(value);
  return Boolean(token && token.value);
}

export function getPropertiesSuggestions(
  modifier: ModifiersEnum | undefined,
): ReadonlyArray<InboxesSearchSuggestionItemProps> {
  const values = getPropertiesValues(modifier);
  const placeholders = getPropertiesPlaceholders(modifier);

  return [
    ...values.map((value) => ({value: `${modifier}:${value}`})),
    ...placeholders.map((placeholder) => ({
      key: `${modifier}-${placeholder}`,
      value: `${modifier}:`,
      valuePlaceholder: placeholder,
    })),
  ];
}

function getPropertiesValues(modifier: ModifiersEnum | undefined): Array<string> {
  switch (modifier) {
    case ModifiersEnum.TYPE:
      return TypeValues;

    case ModifiersEnum.HAS:
      return HasValues;

    case ModifiersEnum.IS:
      return IsValues;

    case ModifiersEnum.IN:
      return InValues;

    case ModifiersEnum.BEFORE:
    case ModifiersEnum.DURING:
      return ['today', ...CommonDatesValues];

    case ModifiersEnum.AFTER:
      return ['yesterday', ...CommonDatesValues];

    default:
      throw new Error('Property modifier does not exist');
  }
}

function getPropertiesPlaceholders(modifier: ModifiersEnum | undefined): Array<string> {
  switch (modifier) {
    case ModifiersEnum.BEFORE:
    case ModifiersEnum.DURING:
    case ModifiersEnum.AFTER:
      return CommonDatesPlaceholders;

    default:
      return [];
  }
}
